import React, { useState, useEffect, useCallback } from 'react';
import { 
  IRadioComponent,
  IFormControlChange,
  IFormValidationErrors
} from '../df-form-preview-interfaces';
import { DfFormErrorMsg } from '../df-form-error-msg';
import { DfFormComments } from '../df-form-comments';

interface DfFormRadioProps {
  id: string;
  properties: IRadioComponent;
  validationErrors?: IFormValidationErrors;
  formValue?: string;
  readonly?: boolean;
  disabled?: boolean;
  touchedFields?: Record<string, boolean>;
  formSubmitted?: boolean;
  mode?: 'edit' | 'preview' | 'test';
  onValueChange?: (change: IFormControlChange) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  hideLabel?: boolean;
  showCommentsInPreview?: boolean;
}

export const DfFormRadio: React.FC<DfFormRadioProps> = ({
  id,
  properties,
  validationErrors = {},
  formValue = '',
  readonly = false,
  disabled = false,
  touchedFields = {},
  formSubmitted = false,
  mode = 'preview',
  onValueChange,
  onBlur,
  onFocus,
  className = '',
  hideLabel = false,
  showCommentsInPreview = false
}) => {
  const [selectedValue, setSelectedValue] = useState<string>(formValue);
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [comment, setComment] = useState<string>('');
  const [showCommentInput, setShowCommentInput] = useState<boolean>(false);

  // Initialize comment from properties when in preview mode (for form submissions)
  useEffect(() => {
    if (mode === 'preview' && showCommentsInPreview && properties?.basic?.comments) {
      setComment(properties.basic.comments);
    }
  }, [mode, showCommentsInPreview, properties]);

  // Validate field
  const validateField = useCallback((value: string): { isValid: boolean; errors: Record<string, any> } => {
    const errors: Record<string, any> = {};
    let isValid = true;

    // Required validation
    if (properties?.validation?.required && (!value || value.trim() === '')) {
      errors.required = true;
      isValid = false;
    }

    return { isValid, errors };
  }, [properties]);

  // Handle value changes
  const handleValueChange = useCallback((newValue: string) => {
    setSelectedValue(newValue);
    
    if (onValueChange) {
      const validation = validateField(newValue);
      const change: IFormControlChange = {
        id,
        value: newValue,
        isValid: validation.isValid,
        errors: validation.errors,
        comments: comment
      };
      onValueChange(change);
    }
  }, [id, onValueChange, validateField, comment]);

  // Helper function to check if a value should show red chip (fail/no)
  const shouldShowRedChip = (value: string): boolean => {
    const lowerValue = value.toLowerCase();
    return lowerValue === 'no' || lowerValue === 'fail' || lowerValue === 'false';
  };

  // Check if comments section should be shown
  const shouldShowCommentsSection = (): boolean => {
    return shouldShowRedChip(selectedValue);
  };

  // Check if comment input should be shown
  const shouldShowCommentInput = (): boolean => {
    // Don't show comment input in preview mode
    if (mode === 'preview') {
      return false;
    }
    const needsComments = shouldShowCommentsSection();
    const hasNoComment = !comment || comment.trim() === '';
    return needsComments && (showCommentInput || hasNoComment);
  };

  // Check if comment display should be shown
  const shouldShowCommentDisplay = (): boolean => {
    // In preview mode, show comments if they exist and showCommentsInPreview is true
    if (mode === 'preview' && showCommentsInPreview && comment) {
      return true;
    }
    return shouldShowCommentsSection() && !showCommentInput && !!comment;
  };

  // Toggle comment input visibility
  const toggleCommentInput = () => {
    // Don't allow toggling in preview mode
    if (mode === 'preview') {
      return;
    }
    setShowCommentInput(!showCommentInput);
  };

  // Handle comment save
  const handleCommentSave = (newComment: string) => {
    setComment(newComment);
    setShowCommentInput(false);
    
    if (onValueChange) {
      const validation = validateField(selectedValue);
      const change: IFormControlChange = {
        id,
        value: selectedValue,
        isValid: validation.isValid,
        errors: validation.errors,
        comments: newComment
      };
      onValueChange(change);
    }
  };

  // Handle radio change
  const handleRadioChange = (optionValue: string) => {
    handleValueChange(optionValue);
  };

  // Handle focus
  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
  };

  // Handle blur
  const handleBlur = () => {
    setIsTouched(true);
    if (onBlur) {
      onBlur();
    }
  };

  // Update touched fields
  useEffect(() => {
    if (isTouched) {
      touchedFields[id] = true;
    }
  }, [isTouched, id, touchedFields]);

  // Update value when formValue prop changes
  useEffect(() => {
    if (formValue !== selectedValue) {
      setSelectedValue(formValue);
    }
  }, [formValue, selectedValue]);

  // Mark as touched when form is submitted
  useEffect(() => {
    if (formSubmitted) {
      setIsTouched(true);
    }
  }, [formSubmitted]);

  // Get label alignment class
  const getLabelAlignmentClass = (): string => {
    const alignment = properties?.styles?.labelAlignment;
    return alignment === 'left' ? 'label-left' : 'label-top';
  };

  // Get layout class for inline/vertical layout
  const getLayoutClass = (): string => {
    return properties?.basic?.inlineLayout ? 'inline-layout' : 'vertical-layout';
  };


  const shouldDisable = disabled || readonly || properties?.validation?.readonly;

  return (
    <>
    <div className={`form-group radio-group ${getLabelAlignmentClass()} ${getLayoutClass()} ${className}`}>
      {!hideLabel && properties.basic.label ? (
        <>
          <label className="form-label">
            {properties.basic.label}
            {properties.validation.required && (
              <span className="required-indicator">*</span>
            )}
          </label>
          
          <div className="input-wrapper">
            <div className={`mt-2 ${properties?.basic?.inlineLayout ? 'inline-options-grid' : 'space-y-2'}`}>
              {(properties.options || []).map((option, index) => (
                <div key={index} className="form-check">
                  <input
                    type="radio"
                    id={`${id}-${index}`}
                    name={id}
                    value={option.value}
                    checked={selectedValue === option.value}
                    onChange={() => handleRadioChange(option.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    disabled={shouldDisable || option.disabled}
                    className="form-check-input"
                  />
                  <label 
                    htmlFor={`${id}-${index}`} 
                    className="form-check-label"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
            <DfFormErrorMsg
        validationErrors={validationErrors}
        fieldId={id}
        touchedFields={touchedFields}
        formSubmitted={formSubmitted}
        properties={properties}
        localValidation={validateField(selectedValue)}
        isTouched={isTouched}
        mode={mode}
      />
          </div>
        </>
      ) : (
        <div className="input-wrapper">
          <div className={`mt-2 ${properties?.basic?.inlineLayout ? 'inline-options-grid' : 'space-y-2'}`}>
            {(properties.options || []).map((option, index) => (
              <div key={index} className="form-check">
                <input
                  type="radio"
                  id={`${id}-${index}`}
                  name={id}
                  value={option.value}
                  checked={selectedValue === option.value}
                  onChange={() => handleRadioChange(option.value)}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  disabled={shouldDisable || option.disabled}
                  className="form-check-input"
                />
                <label 
                  htmlFor={`${id}-${index}`} 
                  className="form-check-label"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
          <DfFormErrorMsg
        validationErrors={validationErrors}
        fieldId={id}
        touchedFields={touchedFields}
        formSubmitted={formSubmitted}
        properties={properties}
        localValidation={validateField(selectedValue)}
        isTouched={isTouched}
        mode={mode}
      />
        </div>
      )}

   

    </div>
       {/* Comments Section - Below radio options */}
       {(!shouldDisable || (mode === 'preview' && showCommentsInPreview)) && (
        <>
          {/* Show saved comment display when not in comment input mode */}
          {shouldShowCommentDisplay() && (
            <div onClick={mode === 'preview' ? undefined : toggleCommentInput} style={{ cursor: mode === 'preview' ? 'default' : 'pointer' }}>
              <label className="form-label notes-label-text">
                Notes: {comment}
              </label>
            </div>
          )}

          {/* Show comment input when needed */}
          {shouldShowCommentInput() && (
            <DfFormComments
              comment={comment}
              onSave={handleCommentSave}
              placeholder="Enter your reason..."
              disabled={shouldDisable}
            />
          )}
        </>
      )}
    </>
  );
};

export default DfFormRadio;
